import tenantShortIds from '../config/tenantShortIds.json';
import { Base62 } from './base62';

/** Slug → short id for compact /s/… QR URLs. Source: regenesis-backend/src/config/tenantShortIds.json (run backend `npm run sync:tenant-short-ids` after edits). */
export const tenantToIdMap: { [key: string]: number } = tenantShortIds;

const SHORT_CODE_RE =
  /^([0-9a-zA-Z]+)-([0-9a-zA-Z]+)-([0-9a-zA-Z]+)$/;
const LEGACY_SHORT_CODE_RE =
  /^([0-9a-zA-Z])([0-9a-zA-Z]+)-([0-9a-zA-Z]+)$/;

export function getTenantId(slug: string): number | null {
  return tenantToIdMap[slug] ?? null;
}

/**
 * Reverse lookup. Shared short ids (regenesis/qa/app → 0) prefer regenesis
 * so we never send `app`/`qa` as x-tenant-id.
 */
export function getTenantSlug(id: number): string | null {
  const matches = Object.entries(tenantToIdMap)
    .filter(([, shortId]) => Number(shortId) === Number(id))
    .map(([slug]) => slug);

  if (matches.length === 0) {
    return null;
  }
  if (matches.includes('regenesis')) {
    return 'regenesis';
  }
  return matches[0];
}

export function normalizeTenantSlug(
  slug: string | null | undefined,
): string | null {
  if (!slug) {
    return null;
  }
  if (slug === 'app' || slug === 'qa') {
    return 'regenesis';
  }
  return slug;
}

export function parseShortCode(
  value: string | null | undefined,
): { tenantEncoded: string; productEncoded: string; itemEncoded: string } | null {
  if (!value) {
    return null;
  }
  const trimmed = String(value).trim();
  const match =
    trimmed.match(SHORT_CODE_RE) || trimmed.match(LEGACY_SHORT_CODE_RE);
  if (!match) {
    return null;
  }
  return {
    tenantEncoded: match[1],
    productEncoded: match[2],
    itemEncoded: match[3],
  };
}

/** Tenant encoded in a compact QR / path segment, e.g. 6-1-1o → sapphire. */
export function resolveTenantFromShortCode(
  value: string | null | undefined,
): string | null {
  const parsed = parseShortCode(value);
  if (!parsed) {
    return null;
  }
  return normalizeTenantSlug(
    getTenantSlug(Base62.decode(parsed.tenantEncoded)),
  );
}

/**
 * Short-code tenant always wins over a default/query tenant.
 * 14-digit codes keep query x-tenant-id, then regenesis.
 */
export function resolveScanTenantId(
  qrCode: string | null | undefined,
  queryTenantId?: string | null,
): string {
  return (
    resolveTenantFromShortCode(qrCode) ||
    normalizeTenantSlug(queryTenantId) ||
    'regenesis'
  );
}

export function extractQrCodeAndTenant(
  raw: string,
): { qrCode: string; tenantId: string } | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) {
    return null;
  }

  const rawShortTenant = resolveTenantFromShortCode(trimmed);
  if (rawShortTenant) {
    return { qrCode: trimmed, tenantId: rawShortTenant };
  }

  try {
    const url = new URL(trimmed);
    const pathParts = url.pathname.split('/').filter((part) => part.length > 0);
    const queryTenant = normalizeTenantSlug(url.searchParams.get('x-tenant-id'));
    const qrFromQuery = url.searchParams.get('qrcode');

    if (pathParts[0] === 's' && pathParts[1]) {
      const shortCode = pathParts[1];
      const tenantId =
        resolveTenantFromShortCode(shortCode) || queryTenant;
      if (tenantId) {
        return { qrCode: shortCode, tenantId };
      }
    }

    if (qrFromQuery) {
      const tenantId =
        resolveTenantFromShortCode(qrFromQuery) || queryTenant;
      if (tenantId) {
        return { qrCode: qrFromQuery, tenantId };
      }
    }

    const detailsIndex = pathParts.indexOf('details');
    if (detailsIndex !== -1 && detailsIndex + 1 < pathParts.length) {
      const qrCode = pathParts[detailsIndex + 1];
      const tenantId = resolveTenantFromShortCode(qrCode) || queryTenant;
      if (tenantId) {
        return { qrCode, tenantId };
      }
    }

    // Only treat a path segment as the QR when it is a compact short code,
    // or a single-segment URL like /00000000000001?x-tenant-id=dpp.
    // Using the last segment of any multi-part path would misread
    // /product/... or /01/... URLs as the QR code.
    const pathQr = pathParts[pathParts.length - 1];
    if (pathQr) {
      const shortCodeTenant = resolveTenantFromShortCode(pathQr);
      if (shortCodeTenant) {
        return { qrCode: pathQr, tenantId: shortCodeTenant };
      }
      if (queryTenant && pathParts.length === 1) {
        return { qrCode: pathQr, tenantId: queryTenant };
      }
    }

    return null;
  } catch {
    return null;
  }
}
